import type { RolesT } from "@/utils/auth";
import type { SupportedLocale } from "@patient-watch/i18n";

export type WebsiteHelpHubRole = "clinicians" | "secretaries" | "patients";

export type WebsiteHelpGuide = "new-diary" | "make-a-template";

/**
 * Resolve the marketing website origin for help centre links.
 * VITE_WEBSITE_URL is sometimes mis-set to the app host (*.app.*); rewrite those
 * to the public site (dev.patient.watch / patient.watch). Localhost is left alone.
 */
export function getMarketingWebsiteUrl(): string {
    const configured = (import.meta.env.VITE_WEBSITE_URL as string | undefined)?.replace(/\/$/, "");
    const appDomain = (import.meta.env.VITE_DOMAIN as string | undefined)?.replace(/\/$/, "");

    for (const candidate of [configured, appDomain]) {
        if (!candidate) continue;
        const marketingUrl = toMarketingWebsiteUrl(candidate);
        if (marketingUrl) return marketingUrl;
    }

    return "https://patient.watch";
}

export function toMarketingWebsiteUrl(input: string): string | null {
    try {
        const url = new URL(input.includes("://") ? input : `https://${input}`);
        const { hostname } = url;

        if (hostname === "localhost" || hostname === "127.0.0.1") {
            return url.origin;
        }

        let host = hostname
            .replace(/^dev\.app\./, "dev.")
            .replace(/^staging\.app\./, "staging.")
            .replace(/^app\./, "");

        // Marketing site lives on patient.watch, not patient-watch.com / app hosts.
        host = host.replace(/patient-watch\.com$/, "patient.watch");

        return `${url.protocol}//${host}`;
    } catch {
        return null;
    }
}

export function getWebsiteHelpHubRole(roles: RolesT | null | undefined): WebsiteHelpHubRole {
    if (roles?.isClinician) return "clinicians";
    if (roles?.isSecretary) return "secretaries";
    return "patients";
}

export function getWebsiteHelpCentreHref(roles: RolesT | null | undefined, locale: SupportedLocale = "en-GB"): string {
    if (locale !== "en-GB") {
        const appHelpRoute = roles?.isClinician || roles?.isSecretary ? "/help/clinician" : "/help/patient";
        return appHelpRoute;
    }
    return `${getMarketingWebsiteUrl()}/help/${getWebsiteHelpHubRole(roles)}/`;
}

export function getWebsiteHelpCentreAnchorProps(
    roles: RolesT | null | undefined,
    locale: SupportedLocale = "en-GB"
): { href: string; target?: "_blank"; rel?: string } {
    const href = getWebsiteHelpCentreHref(roles, locale);
    if (href.startsWith("/")) {
        return { href };
    }
    return { href, target: "_blank", rel: "noopener noreferrer" };
}

export function getWebsiteHelpGuideHref(roles: RolesT | null | undefined, guide: WebsiteHelpGuide): string {
    const hub = getWebsiteHelpHubRole(roles);
    const origin = getMarketingWebsiteUrl();
    if (hub === "patients") {
        return `${origin}/help/patients/`;
    }
    return `${origin}/help/${hub}/${guide}/`;
}
